
import { useUIStore } from "@/stores/uiStore";
import { cn } from "@/lib/utils";
import { CheckCircle, XCircle, Info, AlertTriangle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export function ToastContainer() {
  const { toasts, removeToast } = useUIStore();

  // H5 FIX: Limit toasts to 4 max
  const visibleToasts = toasts.slice(-4);

  if (visibleToasts.length === 0) return null;

  return (
    <div
      className="fixed top-14 left-3 right-3 z-[100] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence>
        {visibleToasts.map((toast) => (
          <Toast key={toast.id} {...toast} onDismiss={() => removeToast(toast.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function Toast({
  id,
  message,
  type,
  onDismiss,
}: {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "warning";
  onDismiss: () => void;
}) {
  const config = {
    success: {
      icon: <CheckCircle className="size-4 text-profit flex-shrink-0" />,
      bg: "bg-card border-profit/20",
    },
    error: {
      icon: <XCircle className="size-4 text-loss flex-shrink-0" />,
      bg: "bg-card border-loss/20",
    },
    info: {
      icon: <Info className="size-4 text-golden flex-shrink-0" />,
      bg: "bg-card border-golden/20",
    },
    warning: {
      icon: <AlertTriangle className="size-4 text-amber-500 flex-shrink-0" />,
      bg: "bg-card border-amber-500/30",
    },
  };

  const { icon, bg } = config[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.95 }}
      transition={{ duration: 0.25 }}
      className={cn(
        "pointer-events-auto flex items-center gap-2.5 px-4 py-3 rounded-xl border shadow-lg shadow-black/20 backdrop-blur-md",
        bg
      )}
    >
      {icon}
      <span className="text-sm text-foreground flex-1 leading-snug">{message}</span>
      {/* H5 FIX: Dismiss button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="pointer-events-auto flex items-center justify-center size-6 rounded-full hover:bg-surface-hover active:scale-90 transition-spring flex-shrink-0"
        aria-label="Dismiss"
      >
        <X className="size-3 text-muted-foreground" />
      </button>
    </motion.div>
  );
}
